import { uniq } from 'lodash-es';

import { type BaseSchema } from '@blaze-shared/schema-domain';
import { type WorkflowNode } from '@blaze-shared/workflow-domain';

import { WorkflowNodeFactory } from '../factory/node.factory';
import '../nodes'; // ensure all node types are registered in the static NodeRegistry
import { type BaseWorkflowNode } from '../registry/node.registry';

import { extractVariablesFromString } from './parse-string';
import {
  type WorkflowNodeColumnReference,
  type WorkflowNodeColumnReferenceWithNodeId,
  type WorkflowNodeNamedInputReference,
} from './references.models';

/** The columns the given nodes read, as each node's own reference rules see them. */
export function collectColumnReferences(
  nodes: WorkflowNode[],
  schemas: Map<string | symbol, BaseSchema> = new Map(),
): WorkflowNodeColumnReference[] {
  return nodes.flatMap((node) =>
    referencesOf(node, (workflowNode) => workflowNode.getColumnReferences(schemas)),
  );
}

/**
 * Same as `collectColumnReferences`, with the source node's id on each reference.
 * Use when the caller needs to know which node a column ref came from.
 */
export function collectColumnReferencesWithNodeId(
  nodes: WorkflowNode[],
  schemas: Map<string | symbol, BaseSchema> = new Map(),
): WorkflowNodeColumnReferenceWithNodeId[] {
  return nodes.flatMap((node) =>
    referencesOf(node, (workflowNode) => workflowNode.getColumnReferences(schemas)).map((ref) => ({
      ...ref,
      nodeId: node.id,
    })),
  );
}

/** The named workflow inputs the given nodes read. */
export function collectNamedInputReferences(
  nodes: WorkflowNode[],
  schemas: Map<string | symbol, BaseSchema> = new Map(),
): WorkflowNodeNamedInputReference[] {
  return nodes.flatMap((node) =>
    referencesOf(node, (workflowNode) => workflowNode.getNamedInputReferences(schemas)),
  );
}

/** The names of the `@@variables` mentioned anywhere in the given nodes. */
export function collectVariableReferences(nodes: WorkflowNode[]): string[] {
  return uniq(nodes.flatMap((node) => extractVariablesFromString(JSON.stringify(node))));
}

/**
 * A node whose type has no registration contributes nothing rather than failing the batch:
 * callers pass nodes straight from a stored workflow, which may name a type this build no
 * longer knows.
 */
function referencesOf<TReference>(
  node: WorkflowNode,
  read: (workflowNode: BaseWorkflowNode<WorkflowNode>) => TReference[],
): TReference[] {
  try {
    return read(WorkflowNodeFactory.createNode(node, ''));
  } catch {
    return [];
  }
}
